import cityData from '@/generated/cities.json'
import countriesData from '@/generated/countries.json'
import {
  UserBasketSettingsLocationFragment,
  GmServiceLocationInput,
  Address,
  LookupAddressQuery,
  LookupAddressQueryVariables,
  LookupAddressDocument,
  CountryCode,
} from '@/generated/graphql'
import { ApolloClient } from '@apollo/client'

import _ from 'lodash'
import { match } from 'ts-pattern'

import { MenuVendor } from '../domain/menu-vendor'

export const MIN_QUALITY = 0.4 // Random number between 0 and 1 for a "good" address
export const countries = [
  CountryCode.Gb,
  CountryCode.Ie,
  CountryCode.Nl,
  CountryCode.De,
  CountryCode.Au,
  CountryCode.Nz,
  CountryCode.Sg,
]
export const isValidCity = (city: string) =>
  cityData.find((cityOpt) => cityOpt.name.toUpperCase() === city.toUpperCase())?.name

export const groupMinutesWithVendorLocationIds = (vendorLocations: MenuVendor['locations']) =>
  vendorLocations.reduce((acc: { [key: number]: number[] }, location) => {
    if (!location || location === null) return acc
    if (!location.details.isGMActive) return acc
    const maxMinutes = location.details.gmMaxDistanceMinutes || -1
    const listOfIds = acc[maxMinutes] || []
    return { ...acc, [maxMinutes]: [...listOfIds, parseInt(location.id, 10)] }
  }, {})

export const lookupAddressDetails = async (
  address: Pick<Address, 'street' | 'postcode' | 'city'>,
  apolloClient: ApolloClient<object>,
  useGoogle: boolean,
  locale?: string,
): Promise<Address | null> => {
  const query = [address.street, address.city, address.postcode].filter((s) => s).join(', ')
  const response = await apolloClient
    .query<LookupAddressQuery, LookupAddressQueryVariables>({
      query: LookupAddressDocument,
      variables: { query, countries, useGoogle, locale: locale || null },
    })
    .catch(null)
  if (
    response?.data.lookupAddress?.locations.length &&
    response.data.lookupAddress.locations[0].relevance > MIN_QUALITY
  )
    return response.data.lookupAddress.locations[0].address
  return null
}

export const basketLocationToInputFormat = (
  location: UserBasketSettingsLocationFragment,
): GmServiceLocationInput =>
  match(location)
    // TODO: change eslint rule
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .with({ __typename: 'Address' }, ({ __typename, ...address }) => ({
      address: {
        label: address.label,
        flatOrBuilding: address.flatOrBuilding,
        floorOrBuildingName: address.floorOrBuildingName,
        city: address.city,
        country: address.country,
        postcode: address.postcode,
        street: address.street,
        latitude: address.latitude,
        longitude: address.longitude,
      },
    }))
    .with(
      { __typename: 'NewAccountLocation' },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ({ label, address: { __typename, ...restAddress } }) => ({
        address: {
          label,
          flatOrBuilding: restAddress.flatOrBuilding,
          floorOrBuildingName: restAddress.floorOrBuildingName,
          city: restAddress.city,
          country: restAddress.country,
          postcode: restAddress.postcode,
          street: restAddress.street,
          latitude: restAddress.latitude,
          longitude: restAddress.longitude,
        },
      }),
    )
    .with({ __typename: 'AccountLocation' }, ({ id }) => ({
      accountLocation: { id: Number(id) },
    }))
    .exhaustive()

export function countryFriendlyName(country?: string | null): string {
  if (!country || country == null) {
    return ''
  }

  const foundCountry = countriesData.find((countryOpt) => {
    return countryOpt.code === country.toUpperCase()
  })

  if (!foundCountry || foundCountry == null) {
    return country
  }

  return foundCountry.name
}

type AddressStructureRule = {
  [key: string]: {
    prefix: string
    name: keyof Address | 'region'
    label: string
    disabled: boolean
    sx: object
  }
}

const addressStructureRules: AddressStructureRule = {
  floorOrBuildingName: {
    prefix: 'address',
    name: 'floorOrBuildingName',
    label: 'emma:address_floorOrBuildingNameLabel',
    disabled: false,
    sx: { gridColumn: '1 / span 3' },
  },
  flatOrBuilding: {
    prefix: 'address',
    name: 'flatOrBuilding',
    label: 'emma:address_flatOrBuildingLabel',
    disabled: false,
    sx: { gridColumn: '1 / span 3' },
  },
  street: {
    prefix: 'address',
    name: 'street',
    label: 'emma:address_streetLabel',
    disabled: true,
    sx: { gridColumn: '1 / span 3' },
  },
  postcode: {
    prefix: 'address',
    name: 'postcode',
    label: 'emma:address_postcodeLabel',
    disabled: true,
    sx: { gridColumn: { xs: '1 / span 3', md: 'unset' } },
  },
  region: {
    prefix: 'context',
    name: 'region',
    label: 'emma:address_regionLabel',
    disabled: true,
    sx: { gridColumn: { xs: '1 / span 3', md: 'unset' } },
  },
  city: {
    prefix: 'address',
    name: 'city',
    label: 'emma:address_cityLabel',
    disabled: true,
    sx: { gridColumn: { xs: '1 / span 3', md: 'unset' } },
  },
}

export const getAddressStructureFromLocale = (countryCode: string) => {
  const { floorOrBuildingName, flatOrBuilding, street, postcode, region, city } =
    addressStructureRules
  switch (countryCode.toLowerCase()) {
    case 'de':
      return [floorOrBuildingName, street, flatOrBuilding, postcode, region]

    case 'nl':
    case 'it':
    case 'sg':
      return [floorOrBuildingName, street, flatOrBuilding, postcode, city]

    case 'gb':
    case 'ie':
    case 'au':
    case 'nz':
    default:
      return [floorOrBuildingName, flatOrBuilding, street, city, postcode]
  }
}

function addressToLines(address: Address, countryCode?: string | null): string[] {
  return _.compact<string>([
    address.label,
    ...getAddressStructureFromLocale(countryCode ?? 'gb').map(
      (key) => address[key.name === 'region' ? 'city' : key.name] as keyof Address,
    ),
    countryFriendlyName(address.country),
  ])
}

export function addressToSingleLine(address: Address, countryCode?: string | null) {
  return addressToLines(address, countryCode).join(', ')
}

export function addressToLabelAndDetailLines(
  address: Address & { index?: number },
  countryCode?: string | null,
) {
  const lines = addressToLines(address, countryCode)

  if (lines.length === 0) {
    return { label: '', details: '', index: address.index }
  }

  const [label, ...rest] = addressToLines(address)

  return { label, details: rest.join(', '), index: address.index }
}

export function shouldShowStreetBeforeBuildingNumber(locale = 'gb'): boolean {
  return ['de', 'nl', 'it', 'sg'].includes(locale.toLowerCase())
}

export const isSameLocation = (
  orderDetailsLocation: UserBasketSettingsLocationFragment,
  basketLocation: UserBasketSettingsLocationFragment,
) => {
  const { __typename: detailsType } = orderDetailsLocation
  const { __typename: basketType } = basketLocation

  if (detailsType === 'NewAccountLocation' && basketType === 'NewAccountLocation') {
    const detailsLines = addressToSingleLine(orderDetailsLocation.address)
    const basketLines = addressToSingleLine(basketLocation.address)

    return detailsLines === basketLines
  }
  if (detailsType === 'Address' && basketType === 'Address') {
    const detailsLines = addressToSingleLine({ ...orderDetailsLocation, label: null })
    const basketLines = addressToSingleLine({ ...basketLocation, label: null })

    return detailsLines === basketLines
  }
  if (detailsType === 'AccountLocation' && basketType === 'AccountLocation') {
    return orderDetailsLocation.id === basketLocation.id
  }
  return false
}
